import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { userApi, listsApi, autovoteApi } from '../services/api';
import hiveService from '../services/hive';
import { useSettings } from '../context/SettingsContext';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import AutovotingHistory from '../components/AutovotingHistory';
import AutovotingFollowing from '../components/AutovotingFollowing';
import AutovotingAdvanced from '../components/AutovotingAdvanced';
import './Autovoting.css';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

function Autovoting({ onOpenLeftSidebar, onOpenRightSidebar }) {
  const { user, isAuthenticated, loading } = useAuth();
  const { voteDefaultWeight, saveSettings, autovoteGlobalPause, autovoteManaLimit, autovoteAllowSelfVote } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Tab State
  const initialTab = location.pathname.includes('/history') ? 'history' : 
                     location.pathname.includes('/following') ? 'following' : 
                     location.pathname.includes('/advanced') ? 'advanced' : 'settings';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(location.pathname.includes('/history') ? 'history' : 
                 location.pathname.includes('/following') ? 'following' : 
                 location.pathname.includes('/advanced') ? 'advanced' : 'settings');
  }, [location.pathname]);

  // Global Pause State
  const [globalPause, setGlobalPause] = useState(autovoteGlobalPause);

  const [manaLimit, setManaLimit] = useState(autovoteManaLimit);
  const [initialManaLimit, setInitialManaLimit] = useState(autovoteManaLimit);
  const [savingManaLimit, setSavingManaLimit] = useState(false);

  const [allowSelfVote, setAllowSelfVote] = useState(autovoteAllowSelfVote);

  useEffect(() => {
    setGlobalPause(autovoteGlobalPause);
  }, [autovoteGlobalPause]);

  useEffect(() => {
    setManaLimit(autovoteManaLimit);
    setInitialManaLimit(autovoteManaLimit);
  }, [autovoteManaLimit]);

  useEffect(() => {
    setAllowSelfVote(autovoteAllowSelfVote);
  }, [autovoteAllowSelfVote]);

  const handleToggleGlobalPause = async () => {
    const newState = !globalPause;
    setGlobalPause(newState); // optimistic
    const success = await saveSettings({ autovote_global_pause: newState });
    if (!success) {
      setGlobalPause(!newState); // revert
      alert('Failed to update global pause setting.');
    }
  };

  const handleSaveManaLimit = async () => {
    let finalLimit = manaLimit === '' ? 0 : manaLimit;
    setManaLimit(finalLimit);
    setSavingManaLimit(true);
    const success = await saveSettings({ autovote_mana_limit: finalLimit.toString() });
    if (success) {
      setInitialManaLimit(finalLimit);
    } else {
      alert('Failed to update minimum mana limit.');
    }
    setSavingManaLimit(false);
  };

  const handleToggleAllowSelfVote = async (e) => {
    const isChecked = e.target.checked;
    setAllowSelfVote(isChecked);
    await saveSettings({ autovote_allow_self_vote: isChecked.toString() });
  };

  // Posting Key State
  const [hasStoredPostingKey, setHasStoredPostingKey] = useState(false);
  const [clearingKey, setClearingKey] = useState(false);
  const [newPostingKey, setNewPostingKey] = useState('');
  const [newPostingKeyStatus, setNewPostingKeyStatus] = useState('idle'); // 'idle'|'verifying'|'valid'|'invalid'
  const [savingKey, setSavingKey] = useState(false);

  // Stats State
  const [stats, setStats] = useState({ today: 0, thisWeek: 0, thisMonth: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  // Rules State
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);

  // Lists State
  const [lists, setLists] = useState([]);

  // Form State
  const [targetType, setTargetType] = useState('user'); // 'user' or 'list'
  
  // User Search State
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // List Selection State
  const [selectedListId, setSelectedListId] = useState('');

  // Form Fields
  const defaultWeight = voteDefaultWeight ? parseInt(voteDefaultWeight, 10) : 100;

  const [voteArticles, setVoteArticles] = useState(true);
  const [articleWaitTime, setArticleWaitTime] = useState(0);
  const [articleWeight, setArticleWeight] = useState(defaultWeight);
  const [articleDailyLimit, setArticleDailyLimit] = useState(0); // 0 means no limit
  
  const [voteShortContent, setVoteShortContent] = useState(false);
  const [shortPostWaitTime, setShortPostWaitTime] = useState(0);
  const [shortPostWeight, setShortPostWeight] = useState(defaultWeight);
  const [shortPostDailyLimit, setShortPostDailyLimit] = useState(0);
  
  const [voteComments, setVoteComments] = useState(false);
  const [commentWaitTime, setCommentWaitTime] = useState(0);
  const [commentWeight, setCommentWeight] = useState(defaultWeight);
  const [commentDailyLimit, setCommentDailyLimit] = useState(0);

  const [savingRule, setSavingRule] = useState(false);

  // Expandable Sections State
  const [expandedSections, setExpandedSections] = useState({
    keys: false,
    addRule: false,
    rules: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const loadRules = useCallback(async () => {
    if (!user?.username) return;
    setLoadingRules(true);
    try {
      const data = await autovoteApi.getRules(user.username);
      setRules((data.rules || []).filter(r => !r.target_voter && !r.is_advanced));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRules(false);
    }
  }, [user?.username]);

  const loadStats = useCallback(async () => {
    if (!user?.username) return;
    try {
      setLoadingStats(true);
      const data = await autovoteApi.getStats(user.username);
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStats(false);
    }
  }, [user?.username]);

  useEffect(() => {
    if (!user?.username) return;
    
    // Check posting key status
    if (hiveService.getLoginMethod() === 'keychain' || hiveService.getLoginMethod() === 'postingkey') {
      userApi.getStoredPostingKeyStatus(user.username)
        .then(({ hasKey }) => {
          setHasStoredPostingKey(hasKey);
          if (!hasKey) setExpandedSections(prev => ({ ...prev, keys: true }));
        })
        .catch(() => {});
    } else {
       // web2 users don't need posting keys
       setHasStoredPostingKey(true);
    }

    // Load Rules
    loadRules();

    // Load Stats
    loadStats();
    
    // Load Lists
    listsApi.getLists()
      .then(data => setLists(data.lists || []))
      .catch(console.error);
  }, [user?.username, loadRules, loadStats]);

  // --- Posting Key Handlers (Identical to Settings.js) ---
  const handleVerifyNewPostingKey = async () => {
    const val = newPostingKey.trim();
    if (!val) {
      setNewPostingKeyStatus('idle');
      return;
    }
    setNewPostingKeyStatus('verifying');
    try {
      const valid = await hiveService.verifyPostingKey(user.username, val);
      setNewPostingKeyStatus(valid ? 'valid' : 'invalid');
    } catch (err) {
      setNewPostingKeyStatus('invalid');
    }
  };

  const handleSavePostingKey = async () => {
    if (newPostingKeyStatus !== 'valid') return;
    setSavingKey(true);
    try {
      await userApi.storePostingKey(user.username, newPostingKey.trim());
      setHasStoredPostingKey(true);
      setNewPostingKey('');
      setNewPostingKeyStatus('idle');
      setExpandedSections(prev => ({ ...prev, keys: false }));
    } catch (err) {
      alert('Failed to save key: ' + err.message);
    } finally {
      setSavingKey(false);
    }
  };

  const handleClearStoredPostingKey = async () => {
    if (!window.confirm("Are you sure you want to remove your stored posting key? Scheduled posts and autovotes will fail until a new key is provided.")) return;
    
    setClearingKey(true);
    try {
      await userApi.clearStoredPostingKey(user.username);
      setHasStoredPostingKey(false);
      setExpandedSections(prev => ({ ...prev, keys: true }));
    } catch (err) {
      alert('Failed to clear key: ' + err.message);
    } finally {
      setClearingKey(false);
    }
  };

  // --- User Search Handlers ---
  useEffect(() => {
    if (targetType !== 'user') return;
    const q = searchValue.trim().toLowerCase();
    if (q.length < 3) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);

    const timeoutId = setTimeout(async () => {
      try {
        const matches = await hiveService.searchAccounts(q, 5);
        if (cancelled) return;
        setSearchResults(matches);
      } catch (_err) {
        if (cancelled) return;
        setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchValue, targetType]);

  const selectUser = (username) => {
    setSelectedUser(username);
    setSearchValue('');
    setSearchResults([]);
  };

  // --- Rule Handlers ---
  const handleResetForm = () => {
    setSelectedUser(null);
    setSearchValue('');
    setSelectedListId('');
    setArticleWaitTime(0);
    setArticleWeight(defaultWeight);
    setArticleDailyLimit(0);
    setVoteArticles(true);
    setVoteShortContent(false);
    setShortPostWaitTime(0);
    setShortPostWeight(defaultWeight);
    setShortPostDailyLimit(0);
    setVoteComments(false);
    setCommentWaitTime(0);
    setCommentWeight(defaultWeight);
    setCommentDailyLimit(0);
    setTargetType('user');
  };

  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (targetType === 'user' && !selectedUser) {
      alert("Please select a user to autovote.");
      return;
    }
    if (targetType === 'list' && !selectedListId) {
      alert("Please select a list to autovote.");
      return;
    }

    setSavingRule(true);
    try {
      const payload = {
        target_author: targetType === 'user' ? selectedUser : null,
        target_list_id: targetType === 'list' ? parseInt(selectedListId, 10) : null,
        article_wait_time: parseInt(articleWaitTime, 10) || 0,
        article_weight: parseInt(articleWeight, 10) || null,
        article_daily_limit: parseInt(articleDailyLimit, 10) || null,
        vote_articles: voteArticles,
        vote_short_content: voteShortContent,
        short_post_wait_time: parseInt(shortPostWaitTime, 10) || 0,
        short_post_weight: parseInt(shortPostWeight, 10) || null,
        short_post_daily_limit: parseInt(shortPostDailyLimit, 10) || null,
        vote_comments: voteComments,
        comment_wait_time: parseInt(commentWaitTime, 10) || 0,
        comment_weight: parseInt(commentWeight, 10) || null,
        comment_daily_limit: parseInt(commentDailyLimit, 10) || null
      };

      await autovoteApi.saveRule(user.username, payload);
      await loadRules();
      
      handleResetForm();
    } catch (err) {
      alert("Failed to save rule: " + (err.response?.data?.error || err.message));
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this autovote rule?")) return;
    try {
      await autovoteApi.deleteRule(user.username, ruleId);
      await loadRules();
    } catch (err) {
      alert("Failed to delete rule.");
    }
  };

  const handleEditRule = (rule) => {
    if (rule.target_author) {
      setTargetType('user');
      setSelectedUser(rule.target_author);
    } else {
      setTargetType('list');
      setSelectedListId(rule.target_list_id);
    }
    setVoteArticles(rule.vote_articles);
    setArticleWaitTime(rule.article_wait_time);
    setArticleWeight(rule.article_weight === null ? defaultWeight : rule.article_weight);
    setArticleDailyLimit(rule.article_daily_limit || 0);
    
    setVoteShortContent(rule.vote_short_content);
    setShortPostWaitTime(rule.short_post_wait_time);
    setShortPostWeight(rule.short_post_weight === null ? defaultWeight : rule.short_post_weight);
    setShortPostDailyLimit(rule.short_post_daily_limit || 0);
    
    setVoteComments(rule.vote_comments);
    setCommentWaitTime(rule.comment_wait_time);
    setCommentWeight(rule.comment_weight === null ? defaultWeight : rule.comment_weight);
    setCommentDailyLimit(rule.comment_daily_limit || 0);

    setExpandedSections(prev => ({ ...prev, addRule: true }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleRulePause = async (rule) => {
    try {
      const newPausedState = !rule.is_paused;
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, is_paused: newPausedState } : r));
      await autovoteApi.toggleRulePause(user.username, rule.id, newPausedState);
    } catch (err) {
      await loadRules();
      alert("Failed to toggle pause state.");
    }
  };

  // Wait for auth to initialize before making routing decisions
  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>;
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // UI rendering
  return (
    <>
      <SidebarHeader title="Autovoting" onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="layout-container">
        <div className="main-content-wrapper">
        
        <div className="autovoting-page">
          <div className="autovoting-header">
            <h1>Autovoting</h1>
            <p>Automatically upvote posts and comments from specific users or your custom lists.</p>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px' }}>
            <button 
              className={`av-btn ${activeTab === 'settings' ? 'av-btn-primary' : ''}`} 
              onClick={() => { setActiveTab('settings'); navigate('/autovoting'); }}
              style={{ padding: '8px 16px', fontWeight: activeTab === 'settings' ? 600 : 400, background: activeTab === 'settings' ? 'var(--primary-color)' : 'transparent', color: activeTab === 'settings' ? '#fff' : 'var(--text-primary)' }}
            >
              Settings
            </button>
            <button 
              className={`av-btn ${activeTab === 'advanced' ? 'av-btn-primary' : ''}`} 
              onClick={() => { if(hasStoredPostingKey){ setActiveTab('advanced'); navigate('/autovoting/advanced'); } }}
              style={{ padding: '8px 16px', fontWeight: activeTab === 'advanced' ? 600 : 400, background: activeTab === 'advanced' ? 'var(--primary-color)' : 'transparent', color: !hasStoredPostingKey ? 'var(--text-secondary)' : (activeTab === 'advanced' ? '#fff' : 'var(--text-primary)'), opacity: !hasStoredPostingKey ? 0.5 : 1, cursor: !hasStoredPostingKey ? 'not-allowed' : 'pointer' }}
              disabled={!hasStoredPostingKey}
            >
              Advanced
            </button>
            <button 
              className={`av-btn ${activeTab === 'following' ? 'av-btn-primary' : ''}`} 
              onClick={() => { if(hasStoredPostingKey){ setActiveTab('following'); navigate('/autovoting/following'); } }}
              style={{ padding: '8px 16px', fontWeight: activeTab === 'following' ? 600 : 400, background: activeTab === 'following' ? 'var(--primary-color)' : 'transparent', color: !hasStoredPostingKey ? 'var(--text-secondary)' : (activeTab === 'following' ? '#fff' : 'var(--text-primary)'), opacity: !hasStoredPostingKey ? 0.5 : 1, cursor: !hasStoredPostingKey ? 'not-allowed' : 'pointer' }}
              disabled={!hasStoredPostingKey}
            >
              Following
            </button>
            <button 
              className={`av-btn ${activeTab === 'history' ? 'av-btn-primary' : ''}`} 
              onClick={() => { if(hasStoredPostingKey){ setActiveTab('history'); navigate('/autovoting/history'); } }}
              style={{ padding: '8px 16px', fontWeight: activeTab === 'history' ? 600 : 400, background: activeTab === 'history' ? 'var(--primary-color)' : 'transparent', color: !hasStoredPostingKey ? 'var(--text-secondary)' : (activeTab === 'history' ? '#fff' : 'var(--text-primary)'), opacity: !hasStoredPostingKey ? 0.5 : 1, cursor: !hasStoredPostingKey ? 'not-allowed' : 'pointer' }}
              disabled={!hasStoredPostingKey}
            >
              Overview / History
            </button>
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <div className="av-stat-card" style={{ flex: '1 1 min-content', minWidth: '120px', background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Votes Today</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>{loadingStats ? '-' : stats.today}</div>
            </div>
            <div className="av-stat-card" style={{ flex: '1 1 min-content', minWidth: '120px', background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Votes This Week</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>{loadingStats ? '-' : stats.thisWeek}</div>
            </div>
            <div className="av-stat-card" style={{ flex: '1 1 min-content', minWidth: '120px', background: 'var(--bg-secondary)', padding: '16px', borderRadius: '8px', textAlign: 'center', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Votes This Month</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>{loadingStats ? '-' : stats.thisMonth}</div>
            </div>
          </div>

          <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input 
              type="checkbox" 
              id="allowSelfVote" 
              checked={allowSelfVote}
              onChange={handleToggleAllowSelfVote}
            />
            <label htmlFor="allowSelfVote" style={{ cursor: 'pointer', margin: 0, fontWeight: 500 }}>
              Enable Self-Voting <span style={{ fontWeight: 'normal', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>(Allow autovotes on your own posts)</span>
            </label>
          </div>

          {activeTab === 'history' && (
            <AutovotingHistory />
          )}

          {activeTab === 'following' && (
            <AutovotingFollowing />
          )}

          {activeTab === 'advanced' && (
            <AutovotingAdvanced />
          )}

          <div style={{ display: activeTab === 'settings' ? 'block' : 'none' }}>
          
          {hasStoredPostingKey && (
            <div className="av-section" style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '16px', marginBottom: '24px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Global Autovoting Settings</h2>
                  <p style={{ margin: '5px 0 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    {!globalPause ? 'ACTIVE' : 'DISABLED'}
                  </p>
                </div>
                <button
                  type="button"
                  className={`av-pause-toggle ${globalPause ? 'on' : 'off'}`}
                  onClick={handleToggleGlobalPause}
                  aria-label="Toggle global pause"
                >
                  <span className={`av-pause-option ${!globalPause ? 'active' : ''}`}>Enable</span>
                  <span className={`av-pause-option ${globalPause ? 'active' : ''}`}>Disable</span>
                  <span className="av-pause-slider"></span>
                </button>
              </div>
              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Minimum Voting Mana</h3>
                  <p style={{ margin: '5px 0 0', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                    Skip autovoting if your mana drops below this percentage. (0 to disable)
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={manaLimit === '' ? 0 : manaLimit}
                    onChange={(e) => setManaLimit(parseInt(e.target.value, 10))}
                    style={{ width: '200px' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={manaLimit}
                      onChange={(e) => {
                        let valStr = e.target.value;
                        if (valStr === '') {
                           setManaLimit('');
                        } else {
                           let val = parseInt(valStr, 10);
                           if (val > 100) val = 100;
                           if (val < 0) val = 0;
                           setManaLimit(val);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          if ((manaLimit === '' ? 0 : manaLimit) !== initialManaLimit) {
                            handleSaveManaLimit();
                          }
                        }
                      }}
                      className="av-form-control"
                      style={{ width: '60px', textAlign: 'center', padding: '4px' }}
                    />
                    <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>%</span>
                  </div>
                  {(manaLimit === '' ? 0 : manaLimit) !== initialManaLimit && (
                    <button 
                      type="button" 
                      className="av-btn av-btn-primary" 
                      onClick={handleSaveManaLimit}
                      disabled={savingManaLimit}
                      style={{ padding: '6px 12px', fontSize: '0.9rem' }}
                    >
                      {savingManaLimit ? 'Saving...' : 'Save'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {(hiveService.getLoginMethod() === 'keychain' || hiveService.getLoginMethod() === 'postingkey') && (
            <div className="av-section">
              <div 
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => toggleSection('keys')}
              >
                <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Posting Key Requirement</h2>
                <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>{expandedSections.keys ? '▲' : '▼'}</span>
              </div>
              {expandedSections.keys && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                  {hasStoredPostingKey ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                      ✓ A posting key is currently stored and active. Autovotes can be broadcasted on your behalf.
                    </p>
                    <button
                      className="av-btn av-btn-warning"
                      onClick={handleClearStoredPostingKey}
                      disabled={clearingKey}
                    >
                      {clearingKey ? 'Removing…' : 'Clear stored posting key'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0 0 10px' }}>
                    No posting key is currently stored. A posting key is required to cast votes automatically.
                  </p>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                    <input
                      type="password"
                      className="av-form-control"
                      style={{ maxWidth: 300, marginBottom: 0 }}
                      placeholder="Paste private posting key..."
                      value={newPostingKey}
                      onChange={e => {
                        setNewPostingKey(e.target.value);
                        setNewPostingKeyStatus('idle');
                      }}
                    />
                    <button
                      type="button"
                      className="av-btn"
                      style={{ backgroundColor: 'var(--bg-secondary, #333)', color: 'var(--text-primary, #fff)', border: '1px solid var(--border-color)', padding: '10px 16px', whiteSpace: 'nowrap', borderRadius: '4px' }}
                      onClick={handleVerifyNewPostingKey}
                      disabled={newPostingKeyStatus === 'verifying' || !newPostingKey.trim()}
                    >
                      {newPostingKeyStatus === 'verifying' ? 'Checking...' : 'Verify'}
                    </button>
                  </div>
                  {newPostingKeyStatus === 'valid' && <div style={{ fontSize: '0.85rem', color: 'var(--success-color, #28a745)', marginBottom: 12 }}>✓ Valid posting key</div>}
                  {newPostingKeyStatus === 'invalid' && <div style={{ fontSize: '0.85rem', color: 'var(--danger-color, #dc3545)', marginBottom: 12 }}>✗ Invalid posting key</div>}
                  <button
                    className="av-btn av-btn-primary"
                    disabled={newPostingKeyStatus !== 'valid' || savingKey}
                    onClick={handleSavePostingKey}
                  >
                    {savingKey ? 'Saving...' : 'Save Posting Key'}
                  </button>
                </>
              )}
                </div>
              )}
            </div>
          )}

          {hasStoredPostingKey && (
          <>
          <div className="av-section">
            <div 
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggleSection('addRule')}
            >
              <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Add Autovote Rule</h2>
              <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>{expandedSections.addRule ? '▲' : '▼'}</span>
            </div>
            {expandedSections.addRule && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                <form onSubmit={handleSaveRule}>
              <div className="av-type-toggles">
                <button type="button" className={`av-type-btn ${targetType === 'user' ? 'active' : ''}`} onClick={() => setTargetType('user')}>Individual User</button>
                <button type="button" className={`av-type-btn ${targetType === 'list' ? 'active' : ''}`} onClick={() => setTargetType('list')}>User List</button>
              </div>

              {targetType === 'user' && (
                <div className="av-form-group">
                  <label>Target User</label>
                  {selectedUser ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--bg-primary)', padding: '8px 12px', borderRadius: 4, border: '1px solid var(--border-color)', width: 'fit-content' }}>
                      <img src={`https://images.hive.blog/u/${selectedUser}/avatar/small`} alt={selectedUser} style={{ width: 24, height: 24, borderRadius: '50%' }} onError={(e) => { e.target.src = '/default-avatar.png'; }} />
                      <span style={{ fontWeight: 500 }}>@{selectedUser}</span>
                      <button type="button" onClick={() => setSelectedUser(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
                    </div>
                  ) : (
                    <div className="av-search-container">
                      <input
                        type="text"
                        className="av-form-control"
                        placeholder="Search for a Hive user..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        autoComplete="off"
                      />
                      {searching && <div className="av-search-results"><div style={{ padding: '8px 12px' }}>Searching...</div></div>}
                      {!searching && searchValue.length >= 3 && searchResults.length === 0 && (
                        <div className="av-search-results"><div style={{ padding: '8px 12px', color: 'var(--text-secondary)' }}>No matches found.</div></div>
                      )}
                      {!searching && searchResults.length > 0 && (
                        <div className="av-search-results">
                          {searchResults.map(match => (
                            <div key={match} className="av-search-item" onClick={() => selectUser(match)}>
                              <img src={`https://images.hive.blog/u/${match}/avatar/small`} alt={match} className="av-search-avatar" onError={(e) => { e.target.src = '/default-avatar.png'; }} />
                              <span>@{match}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {targetType === 'list' && (
                <div className="av-form-group">
                  <label>Target List</label>
                  <select 
                    className="av-form-control" 
                    value={selectedListId} 
                    onChange={(e) => setSelectedListId(e.target.value)}
                    style={{ maxWidth: 300 }}
                  >
                    <option value="">-- Select a List --</option>
                    {lists.map(list => (
                      <option key={list.id} value={list.id}>{list.name}</option>
                    ))}
                  </select>
                  {lists.length === 0 && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 8 }}>
                      You don't have any lists. <Link to="/lists" style={{ color: 'var(--primary-color)' }}>Create one here.</Link>
                    </p>
                  )}
                </div>
              )}

              <div style={{ marginTop: 24, padding: '16px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <label className="av-checkbox-label" style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: voteArticles ? 16 : 0 }}>
                  <input type="checkbox" checked={voteArticles} onChange={e => setVoteArticles(e.target.checked)} />
                  Enable voting for Flux (Long-form)
                </label>
                {voteArticles && (
                  <div className="av-form-row">
                    <div className="av-form-group">
                      <label>Wait Time (minutes)</label>
                      <input type="number" min="0" className="av-form-control" value={articleWaitTime} onChange={e => setArticleWaitTime(e.target.value)} />
                    </div>
                    <div className="av-form-group">
                      <label>Vote Weight (%)</label>
                      <input type="number" min="1" max="100" className="av-form-control" value={articleWeight} onChange={e => setArticleWeight(e.target.value)} />
                    </div>
                    <div className="av-form-group">
                      <label>Daily Limit (0 = unlimited)</label>
                      <input type="number" min="0" className="av-form-control" value={articleDailyLimit} onChange={e => setArticleDailyLimit(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, padding: '16px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <label className="av-checkbox-label" style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: voteShortContent ? 16 : 0 }}>
                  <input type="checkbox" checked={voteShortContent} onChange={e => setVoteShortContent(e.target.checked)} />
                  Enable voting for Flex (Short posts)
                </label>
                {voteShortContent && (
                  <div className="av-form-row">
                    <div className="av-form-group">
                      <label>Wait Time (minutes)</label>
                      <input type="number" min="0" className="av-form-control" value={shortPostWaitTime} onChange={e => setShortPostWaitTime(e.target.value)} />
                    </div>
                    <div className="av-form-group">
                      <label>Vote Weight (%)</label>
                      <input type="number" min="1" max="100" className="av-form-control" value={shortPostWeight} onChange={e => setShortPostWeight(e.target.value)} />
                    </div>
                    <div className="av-form-group">
                      <label>Daily Limit (0 = unlimited)</label>
                      <input type="number" min="0" className="av-form-control" value={shortPostDailyLimit} onChange={e => setShortPostDailyLimit(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, padding: '16px', background: 'var(--bg-primary)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                <label className="av-checkbox-label" style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: voteComments ? 16 : 0 }}>
                  <input type="checkbox" checked={voteComments} onChange={e => setVoteComments(e.target.checked)} />
                  Enable voting for Comments
                </label>
                {voteComments && (
                  <div className="av-form-row">
                    <div className="av-form-group">
                      <label>Wait Time (minutes)</label>
                      <input type="number" min="0" className="av-form-control" value={commentWaitTime} onChange={e => setCommentWaitTime(e.target.value)} />
                    </div>
                    <div className="av-form-group">
                      <label>Vote Weight (%)</label>
                      <input type="number" min="1" max="100" className="av-form-control" value={commentWeight} onChange={e => setCommentWeight(e.target.value)} />
                    </div>
                    <div className="av-form-group">
                      <label>Daily Limit (0 = unlimited)</label>
                      <input type="number" min="0" className="av-form-control" value={commentDailyLimit} onChange={e => setCommentDailyLimit(e.target.value)} />
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button type="submit" className="av-btn av-btn-primary" disabled={savingRule}>
                  {savingRule ? 'Saving...' : 'Add Rule'}
                </button>
                <button type="button" onClick={handleResetForm} className="av-btn" style={{ backgroundColor: 'var(--bg-tertiary, #2c2c2c)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  Clear
                </button>
              </div>
            </form>
              </div>
            )}
          </div>

          <div className="av-section">
            <div 
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => toggleSection('rules')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>Active Rules</h2>
                {!loadingRules && rules.length > 0 && (
                  <span style={{ background: 'var(--primary-color)', color: '#fff', fontSize: '0.8rem', fontWeight: 600, padding: '2px 8px', borderRadius: '12px' }}>
                    {rules.length}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>{expandedSections.rules ? '▲' : '▼'}</span>
            </div>
            {expandedSections.rules && (
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
                {loadingRules ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>Loading rules...</div>
            ) : rules.length === 0 ? (
              <div className="av-empty-state">
                You haven't set up any autovote rules yet.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="av-rules-table">
                  <thead>
                    <tr>
                      <th>Target</th>
                      <th>Type</th>
                      <th>Flux Options</th>
                      <th>Flex Options</th>
                      <th>Comment Options</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map(rule => (
                      <tr key={rule.id}>
                        <td>
                          {rule.target_author ? (
                            <Link to={`/user/${rule.target_author}`} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'inherit', textDecoration: 'none', fontWeight: 500 }}>
                              <img src={`https://images.hive.blog/u/${rule.target_author}/avatar/small`} alt={rule.target_author} style={{ width: 24, height: 24, borderRadius: '50%' }} onError={(e) => { e.target.src = '/default-avatar.png'; }} />
                              @{rule.target_author}
                            </Link>
                          ) : (
                            <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                              List: {rule.target_list_name}
                            </span>
                          )}
                        </td>
                        <td>
                          <span style={{ fontSize: '0.85rem', background: 'var(--bg-primary)', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-color)' }}>
                            {rule.target_author ? 'User' : 'List'}
                          </span>
                        </td>
                        <td>
                          {rule.vote_articles ? (
                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              Wait: {rule.article_wait_time}m | Weight: {rule.article_weight}%<br/>
                              Limit: {rule.article_daily_limit || 'None'}
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Disabled</span>
                          )}
                        </td>
                        <td>
                          {rule.vote_short_content ? (
                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              Wait: {rule.short_post_wait_time}m | Weight: {rule.short_post_weight}%<br/>
                              Limit: {rule.short_post_daily_limit || 'None'}
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Disabled</span>
                          )}
                        </td>
                        <td>
                          {rule.vote_comments ? (
                            <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              Wait: {rule.comment_wait_time}m | Weight: {rule.comment_weight}%<br/>
                              Limit: {rule.comment_daily_limit || 'None'}
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Disabled</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-start', height: '100%' }}>
                            <button onClick={() => handleToggleRulePause(rule)} className="av-btn" style={{ padding: '6px 12px', fontSize: '0.85rem', backgroundColor: rule.is_paused ? 'var(--success-color, #28a745)' : 'var(--warning-color, #ffc107)', color: '#fff', border: 'none' }}>
                              {rule.is_paused ? 'Resume' : 'Pause'}
                            </button>
                            <button onClick={() => handleEditRule(rule)} className="av-btn av-btn-primary" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                              Edit
                            </button>
                            <button onClick={() => handleDeleteRule(rule.id)} className="av-btn av-btn-danger" style={{ padding: '6px 12px', fontSize: '0.85rem' }}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
              </div>
            )}
          </div>
          </>)}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default Autovoting;
